import { CheckCircleOutlined, DollarOutlined, TagsOutlined, TeamOutlined } from '@ant-design/icons';
import { Alert, Card, Col, Empty, Progress, Row, Spin, Statistic } from 'antd';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { attendeeTypeLabelKey } from '../i18n/attendeeType';
import { textLocaleTag } from '../i18n/locale';
import { registrationStatusLabelKey } from '../i18n/registrationStatus';
import { formatPercent } from '../utils/formatPercent';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

interface EventAnalyticsProps {
  eventId: string;
}

interface DashboardData {
  event: {
    id: string;
    title: string;
    maxAttendees: number | null;
  };
  kpis: {
    registrationsTotal: number;
    paidRegistrations: number;
    ticketsTotal: number;
    ticketsCheckedIn: number;
    checkInRate: number;
    capacity: number;
    revenueByCurrency: { currency: string; total: number }[];
  };
  registrationsByStatus: { status: string; count: number }[];
  revenueByCurrency: { currency: string; total: number }[];
  ticketsByType: { name: string; sold: number; capacity: number }[];
  ticketsByAttendeeType: { attendeeType: string; count: number }[];
  registrantMix: { company: number; individual: number };
  registrationsTimeline: { date: string; count: number; cumulative: number }[];
}

// Konzisztens paletta a diagramokhoz (a projekt vermilion/ink hangulatához közel).
const PALETTE = ['#C0473A', '#E08A2B', '#3E7C8C', '#6B8E5A', '#8A6FA3', '#B0A24E', '#5B5147'];

export function EventAnalytics({ eventId }: EventAnalyticsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const numberLocale = textLocaleTag(i18n.language);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/analytics/event/${eventId}/dashboard`);
        if (!cancelled) setData(response.data);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || t('eventAnalytics.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  // Jegyek regisztráció nélkül is létezhetnek (CSV-import), ezért csak akkor
  // üres, ha se regisztráció, se jegy nincs.
  if (!data || (data.kpis.registrationsTotal === 0 && data.kpis.ticketsTotal === 0)) {
    return <Empty description={t('eventAnalytics.empty')} />;
  }

  const { kpis } = data;

  const primaryRevenue = kpis.revenueByCurrency[0];
  const revenueLabel = primaryRevenue
    ? `${primaryRevenue.total.toLocaleString(numberLocale)} ${primaryRevenue.currency}`
    : '—';

  const statusChart = {
    labels: data.registrationsByStatus.map((s) => {
      const key = registrationStatusLabelKey(s.status);
      return key ? t(key) : s.status;
    }),
    datasets: [
      {
        data: data.registrationsByStatus.map((s) => s.count),
        backgroundColor: data.registrationsByStatus.map((_, i) => PALETTE[i % PALETTE.length]),
        borderWidth: 0,
      },
    ],
  };

  const ticketTypeChart = {
    labels: data.ticketsByType.map((t) => t.name),
    datasets: [
      {
        label: t('eventAnalytics.legendSold'),
        data: data.ticketsByType.map((t) => t.sold),
        backgroundColor: '#C0473A',
      },
      {
        label: t('eventAnalytics.legendCapacity'),
        data: data.ticketsByType.map((t) => t.capacity),
        backgroundColor: '#E0D6C4',
      },
    ],
  };

  const timelineChart = {
    labels: data.registrationsTimeline.map((p) => p.date),
    datasets: [
      {
        label: t('eventAnalytics.legendCumulative'),
        data: data.registrationsTimeline.map((p) => p.cumulative),
        borderColor: '#C0473A',
        backgroundColor: 'rgba(192, 71, 58, 0.12)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: t('eventAnalytics.legendDaily'),
        data: data.registrationsTimeline.map((p) => p.count),
        borderColor: '#3E7C8C',
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  const mixChart = {
    labels: [t('eventAnalytics.mixCompany'), t('eventAnalytics.mixIndividual')],
    datasets: [
      {
        data: [data.registrantMix.company, data.registrantMix.individual],
        backgroundColor: ['#3E7C8C', '#E08A2B'],
        borderWidth: 0,
      },
    ],
  };

  const capacityPct =
    kpis.capacity > 0 ? Math.min(100, Math.round((kpis.ticketsTotal / kpis.capacity) * 100)) : 0;

  return (
    <div>
      {/* KPI kártyák */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('eventAnalytics.kpiRegistrations')}
              value={kpis.registrationsTotal}
              prefix={<TeamOutlined />}
            />
            <div style={{ color: '#8a7e6f', fontSize: 12, marginTop: 4 }}>
              {t('eventAnalytics.kpiPaidOf', { count: kpis.paidRegistrations })}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('eventAnalytics.kpiTickets')}
              value={kpis.ticketsTotal}
              prefix={<TagsOutlined />}
            />
            {kpis.capacity > 0 && <Progress percent={capacityPct} size="small" showInfo={false} />}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('eventAnalytics.kpiCheckedIn')}
              value={kpis.ticketsCheckedIn}
              suffix={`/ ${kpis.ticketsTotal}`}
              prefix={<CheckCircleOutlined />}
            />
            <Progress
              percent={kpis.checkInRate}
              size="small"
              format={() => formatPercent(kpis.checkInRate, i18n.language)}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={t('eventAnalytics.kpiRevenue')}
              value={revenueLabel}
              prefix={<DollarOutlined />}
            />
            {kpis.revenueByCurrency.length > 1 && (
              <div style={{ color: '#8a7e6f', fontSize: 12, marginTop: 4 }}>
                {kpis.revenueByCurrency
                  .slice(1)
                  .map((r) => `${r.total.toLocaleString(numberLocale)} ${r.currency}`)
                  .join(' · ')}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Diagramok */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={t('eventAnalytics.chartStatus')}>
            <div style={{ maxWidth: 360, margin: '0 auto' }}>
              <Doughnut
                data={statusChart}
                options={{ plugins: { legend: { position: 'bottom' } } }}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t('eventAnalytics.chartMix')}>
            <div style={{ maxWidth: 360, margin: '0 auto' }}>
              <Doughnut data={mixChart} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title={t('eventAnalytics.chartTimeline')}>
            <Line
              data={timelineChart}
              options={{
                responsive: true,
                plugins: { legend: { position: 'bottom' } },
                scales: { y: { beginAtZero: true } },
              }}
            />
          </Card>
        </Col>
      </Row>

      {data.ticketsByType.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title={t('eventAnalytics.chartTicketTypes')}>
              <Bar
                data={ticketTypeChart}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'bottom' } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {data.ticketsByAttendeeType.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title={t('eventAnalytics.chartAttendeeTypes')} size="small">
              <Row gutter={16}>
                {data.ticketsByAttendeeType.map((a) => {
                  // #165 — a KÖZÖS címketárból, nem egy itt tartott
                  // négyértékű másolatból. A statisztika volt a panel egyetlen
                  // helye, ahol kategória-felirat egyáltalán létezett.
                  //
                  // Ismeretlen értékre a NYERS érték marad a felirat, ahogy
                  // eddig: a statisztika azt írja ki, ami az adatbázisban áll.
                  const labelKey = attendeeTypeLabelKey(a.attendeeType);
                  return (
                    <Col key={a.attendeeType}>
                      <Statistic title={labelKey ? t(labelKey) : a.attendeeType} value={a.count} />
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default EventAnalytics;
